import { useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  AdminPatchTenantRequest,
  AdminReassignPlanTierRequest,
  AdminSuspendTenantRequest,
} from '@naya/shared'
import { adminApi } from '../adminApi.js'
import type { TenantRow } from '../types.js'
import { tenantDetailKey, tenantAuditKey, tenantsListKey } from './useTenants.js'

interface TenantResponse {
  tenant: TenantRow
}

/** Invalidates everything that depends on a single tenant's state. */
function invalidateTenant(
  qc: ReturnType<typeof useQueryClient>,
  tenantId: string,
) {
  void qc.invalidateQueries({ queryKey: tenantDetailKey(tenantId) })
  void qc.invalidateQueries({ queryKey: tenantAuditKey(tenantId) })
  void qc.invalidateQueries({ queryKey: tenantsListKey })
}

/** PATCH /tenants/:id — name, notes, billing cycle. */
export function usePatchTenant(tenantId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: AdminPatchTenantRequest) =>
      adminApi.patch<TenantResponse>(`/tenants/${tenantId}`, patch),
    onSuccess: () => invalidateTenant(qc, tenantId),
  })
}

/** PATCH /tenants/:id/plan-tier — schedules a reassignment for next period. */
export function useReassignPlanTier(tenantId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: AdminReassignPlanTierRequest) =>
      adminApi.patch<TenantResponse>(`/tenants/${tenantId}/plan-tier`, input),
    onSuccess: () => invalidateTenant(qc, tenantId),
  })
}

export function useSuspendTenant(tenantId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: AdminSuspendTenantRequest) =>
      adminApi.post<TenantResponse>(`/tenants/${tenantId}/suspend`, input),
    onSuccess: () => invalidateTenant(qc, tenantId),
  })
}

export function useUnsuspendTenant(tenantId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      adminApi.post<TenantResponse>(`/tenants/${tenantId}/unsuspend`),
    onSuccess: () => invalidateTenant(qc, tenantId),
  })
}

interface ImpersonateResponse {
  acting_as: {
    user_id: string
    tenant_id: string
    display_name: string
    email: string | null
    started_at: string
  }
}

/**
 * Starts impersonation of a tenant's owner. The backend sets the impersonation
 * cookie alongside the admin cookie; the caller opens the owner UI in a new
 * tab. We invalidate the tenant audit log so the impersonation_started row
 * shows up on return.
 */
export function useImpersonateOwner(tenantId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ownerUserId: string) =>
      adminApi.post<ImpersonateResponse>(`/tenants/${tenantId}/impersonate`, {
        user_id: ownerUserId,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tenantAuditKey(tenantId) })
    },
  })
}
